import { TextField, Typography, FormControl, Select, MenuItem } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

import { IconInfoCircle } from "@tabler/icons";

import { useEffect, useRef, useState } from "react";
import { defaultLanguage, languages } from "../../refs";
// @ts-ignore
import c from "./input.module.css";

interface InputProps {
  id?: string;
  value: any;
  onChange?: (value: any) => void;
  variant?: "standard" | "filled" | "outlined";
  type?: "text" | "password" | "number" | "phone" | "email";
  label?: string;
  warn?: boolean;
  warning?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  onEnterPress?: () => void;
  multiline?: boolean;
  customAdormentEnd?: React.ReactNode;
  customAdormentStart?: React.ReactNode;
  info?: string;
  isZoomLevel?: boolean;
  i18n?: boolean;
  minNumber?: number;
  maxNumber?: number;
  onBlur?: () => void;
  autoFocus?: boolean;
  autoComplete?: string;
  disableWheelEvent?: boolean;
  mandatory?: boolean;
  placeholder?: string;
  subLabel?: string;
}

export default function Input({
  id,
  value,
  onChange = () => {},
  type = "text",
  label,
  warn,
  warning,
  disabled,
  size = "medium",
  onEnterPress = () => {},
  multiline,
  customAdormentEnd,
  customAdormentStart,
  info,
  isZoomLevel,
  i18n,
  minNumber,
  maxNumber,
  onBlur,
  autoFocus,
  autoComplete,
  disableWheelEvent,
  mandatory,
  placeholder,
  subLabel,
}: InputProps) {
  const ref = useRef<HTMLInputElement>();
  const [show, setShow] = useState(false);
  const [selectedLang, setSelectedLang] = useState(defaultLanguage.current);

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.querySelector("input")?.focus();
      ref.current.querySelector("input")?.select();
    }
    if (disableWheelEvent) {
      document.addEventListener("wheel", disableScroll);
    }

    return () => {
      document.removeEventListener("wheel", disableScroll);
    };
  }, []);

  function disableScroll(event) {
    if (event.target.type === "number") {
      event.target.blur();
      event.stopPropagation();
    }
  }

  const CustomWidthTooltip = styled(({ className, ...props }: any) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
    />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 500,
    },
  });

  let inputProps: any = {
    id,
  };

  if (i18n) {
    inputProps.startAdornment = (
      <InputAdornment position="start">
        <FormControl
          sx={{ minWidth: 50, ml: 0, marginBottom: "-1px" }}
          size="small"
          variant="outlined"
        >
          <Select
            labelId="select-lang-label"
            id="select-lang"
            value={selectedLang}
            style={{ borderWidth: 0, borderColor: "lime" }}
            onChange={(e) => setSelectedLang(e.target.value)}
          >
            {languages.current.map((language) => {
              return (
                <MenuItem
                  key={"input-language-option-" + language.id}
                  value={language.iso_code}
                >
                  {language.short_name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </InputAdornment>
    );
  }

  if (type === "phone") {
    inputProps.inputMode = "numeric";
    inputProps.pattern = "[0-9]*";
    inputProps.startAdornment = <InputAdornment position="start">+</InputAdornment>;
  } else if (type === "password") {
    inputProps.endAdornment = (
      <InputAdornment position="end">
        <IconButton
          className={c.eye} //? cant apply module class type
          style={{ width: "2em", marginBottom: "0", marginRight: "1px" }}
          aria-label="toggle password visibility"
          onClick={() => {
            setShow(!show);
          }}
          onMouseDown={() => {}}
          edge="end"
        >
          {show ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    );
  } else if (customAdormentEnd) {
    inputProps.endAdornment = <InputAdornment position="end">{customAdormentEnd}</InputAdornment>;
  } else if (customAdormentStart) {
    inputProps.startAdornment = (
      <InputAdornment position="start">{customAdormentStart}</InputAdornment>
    );
  } else if (info) {
    inputProps.endAdornment = (
      <InputAdornment position="end">
        <CustomWidthTooltip
          title={info}
          placement="right"
        >
          <IconButton
            className={c.info} //? cant apply module class type
            style={{ width: "2em", marginBottom: "0", marginRight: "1px" }}
            edge="end"
          >
            <IconInfoCircle
              stroke={2}
              color="rgb(94, 53, 177)"
            />
          </IconButton>
        </CustomWidthTooltip>
      </InputAdornment>
    );
  }
  if (isZoomLevel) {
    inputProps.startAdornment = (
      <InputAdornment position="start">
        <Typography
          variant="overline"
          display="block"
          style={{ fontSize: "20px" }}
        >
          %
        </Typography>
      </InputAdornment>
    );
  }

  if (type === "number") {
    inputProps.inputProps = {
      min: minNumber,
      max: maxNumber,
    };
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onEnterPress();
    }
    if (type === "phone") {
      if (value.length < 1 && e.key !== "5") {
        e.stopPropagation();
        e.preventDefault();
        return false;
      }
      if (value.replace(/ /g, "").length === 10) {
        e.stopPropagation();
        e.preventDefault();
        return false;
      }
    }
  };

  let warningText = "";
  if (warn && warning) {
    warningText = warning;
  } else if (warn) {
    warningText = "This field is manadory ";
    if (i18n) {
      languages.current.forEach((language) => {
        if (!value[language.iso_code]) {
          warningText += "(" + language.short_name + ") ";
        }
      });
    }
  }

  function handleMultilanguageChange(event) {
    onChange((prev) => ({ ...prev, [selectedLang]: event.target.value }));
  }

  return (
    <div className={c.container}>
      <TextField
        ref={ref}
        className={c.input}
        autoComplete={autoComplete}
        value={i18n ? value[selectedLang] : value}
        onChange={(e) => (i18n ? handleMultilanguageChange(e) : onChange(e.target.value))}
        label={
          <span>
            {label} {mandatory && <span style={{ color: "red", fontSize: "20px" }}>*</span>}
          </span>
        }
        variant="outlined"
        disabled={disabled}
        onKeyPress={(e) => handleKeyPress(e)}
        onBlur={onBlur}
        type={type === "password" ? (show ? "text" : "password") : type}
        error={warn}
        helperText={warningText}
        InputProps={inputProps}
        size={size}
        multiline={multiline}
        rows={multiline ? 4 : 1}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (
            type === "number" &&
            (e.key === "e" || e.key === "-" || e.key === "+" || e.key === "." || e.key === ",")
          )
            e.preventDefault();
        }}
      />
      {subLabel && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginTop: "4px", marginLeft: "4px", display: "block" }}
        >
          {subLabel}
        </Typography>
      )}
    </div>
  );
}
